import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { RxCross2 } from "react-icons/rx";

import { CardCategoryEnum } from "@sorcery/db";
import { Button, Checkbox, H3, InternalLink, Line, P, VariantSwitcher, toast } from "@sorcery/ui";

import { FAQList } from "~/components/molecules/FAQList";
import { CardGalleryImage } from "~/components/molecules/card/CardGalleryImage";
import { DeckQuantitySelector } from "~/components/molecules/deck/DeckQuantitySelector";
import { useModal } from "~/providers/ModalProvider";
import { fullCollectionlistJotai } from "~/state/collectionJotai";
import {
  avatarJotai,
  deckJotai,
  decklistJotai,
  maybeboardJotai,
  sideboardJotai,
  trackingJotai,
} from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import { cardPath } from "~/utils/paths";
import { api } from "~/utils/trpc";
import { CollectionTrackingMessage } from "../atoms/CollectionTrackingMessage";
import { VariantLabel } from "../atoms/VariantLabel";
import { CardFlipper } from "../molecules/card/CardFlipper";

export const DeckGallery = ({ card }: { card: CardType }) => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const avatar = useAtomValue(avatarJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const sideboard = useAtomValue(sideboardJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const maybeboard = useAtomValue(maybeboardJotai)!;
  const [error, setError] = useState("");
  const { closeModal } = useModal();
  const trpcUtils = api.useUtils();

  /**
   * collection management
   */
  const tracking = useAtomValue(trackingJotai);
  const fullCollectionlist = useAtomValue(fullCollectionlistJotai);
  const itemsInCollection = useMemo(
    () =>
      tracking
        ? (fullCollectionlist
            ?.find((c) => c.card.id === card.id)
            ?.groupings.reduce((acc, grouping) => acc + grouping.items.length, 0) ?? 0)
        : undefined,
    [tracking, fullCollectionlist, card.id],
  );

  const [decklistQuantity, setDecklistQuantity] = useState(
    decklist.find((dc) => dc.cardId === card.id)?.quantity ?? 0,
  );
  const dlqChanged = decklist.find((dc) => dc.cardId === card.id)?.quantity !== decklistQuantity;
  const [sideboardQuantity, setSideboardQuantity] = useState(
    sideboard.find((dc) => dc.cardId === card.id)?.quantity ?? 0,
  );
  const sbqChanged = sideboard.find((dc) => dc.cardId === card.id)?.quantity !== sideboardQuantity;
  const [maybeboardQuantity, setMaybeboardQuantity] = useState(
    maybeboard.find((dc) => dc.cardId === card.id)?.quantity ?? 0,
  );
  const mbqChanged =
    maybeboard.find((dc) => dc.cardId === card.id)?.quantity !== maybeboardQuantity;
  const [decklistQuantityMissingError, setDecklistQuantityMissingError] = useState(false);
  const [sideboardQuantityMissingError, setSideboardQuantityMissingError] = useState(false);
  const [maybeboardQuantityMissingError, setMaybeboardQuantityMissingError] = useState(false);
  const [variant, setVariant] = useState(
    avatar.cardId === card.id
      ? avatar.variant
      : (decklist.find((dc) => dc.cardId === card.id)?.variant ??
          sideboard.find((dc) => dc.cardId === card.id)?.variant ??
          maybeboard.find((dc) => dc.cardId === card.id)?.variant ??
          // biome-ignore lint/style/noNonNullAssertion: <explanation>
          card.variants[0]!),
  );
  const variantChanged =
    avatar.cardId === card.id
      ? avatar.variant
      : (decklist.find((dc) => dc.cardId === card.id)?.variant ??
        sideboard.find((dc) => dc.cardId === card.id)?.variant ??
        maybeboard.find((dc) => dc.cardId === card.id)?.variant ??
        // biome-ignore lint/style/noNonNullAssertion: <explanation>
        card.variants[0]! !== variant);
  const hasReverse = !!card.reverse;
  const [isReversed, setIsReversed] = useState(false);

  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const isSite = card.guardian!.category === CardCategoryEnum.Site;

  const toggleVariant = () => {
    setIsReversed(!isReversed);
  };
  const displayedVariant =
    isReversed && hasReverse
      ? // the no reverse on a reverse issue
        (variant.reverse as unknown as VariantType)
      : variant;

  // default to clicked if already feature
  const [isFeature, setIsFeature] = useState(deck.feature.id === variant.id);
  // want it to be feature and wasnt already
  const makeFeature = useMemo(
    () => isFeature && deck.feature.id !== variant.id,
    [deck.feature.id, isFeature, variant.id],
  );
  // default to clicked if already avatar
  const [isMyAvatar, setIsMyAvatar] = useState(
    avatar.cardId === card.id && avatar.variantId === variant.id,
  );
  // want it to be avatar and wasnt already
  const makeAvatar = useMemo(
    () => isMyAvatar && !(avatar.cardId === card.id && avatar.variantId === variant.id),
    [avatar.cardId, avatar.variantId, card.id, isMyAvatar, variant.id],
  );

  const {
    mutateAsync: updateCard,
    error: updateCardError,
    isPending,
  } = api.deck.updateCard.useMutation({
    onSuccess: () => {
      toast("Deck updated");
      (dlqChanged || variantChanged) &&
        void trpcUtils.deck.getDecklistById.invalidate({ id: deck.id });
      (sbqChanged || variantChanged) &&
        void trpcUtils.deck.getSideboardById.invalidate({ id: deck.id });
      (mbqChanged || variantChanged) &&
        void trpcUtils.deck.getMaybeboardById.invalidate({ id: deck.id });
      makeAvatar && void trpcUtils.deck.getAvatarById.invalidate({ id: deck.id });
      makeFeature && void trpcUtils.deck.getById.invalidate({ id: deck.id });
      (dlqChanged || variantChanged || makeAvatar) &&
        void trpcUtils.deck.getHistoryById.invalidate({ id: deck.id });
      closeModal();
    },
  });

  const { data: faqs } = api.card.getFaqs.useQuery({ slug: card.slug });

  // Track any trpc errors for display
  useEffect(() => {
    if (updateCardError) {
      // don't expose server side error messages
      setError("Something went wrong.");
    }
  }, [updateCardError]);

  const onSave = () => {
    setDecklistQuantityMissingError(false);
    setSideboardQuantityMissingError(false);
    setMaybeboardQuantityMissingError(false);

    if (decklistQuantity < 0 || Number.isNaN(decklistQuantity))
      return setDecklistQuantityMissingError(true);
    if (sideboardQuantity < 0 || Number.isNaN(sideboardQuantity))
      return setSideboardQuantityMissingError(true);
    if (maybeboardQuantity < 0 || Number.isNaN(maybeboardQuantity))
      return setMaybeboardQuantityMissingError(true);

    void updateCard({
      deckId: deck.id,
      cardId: card.id,
      decklistQuantity,
      sideboardQuantity,
      maybeboardQuantity,
      variantId: variant.id,
      makeFeature,
      makeAvatar,
    });
  };

  return (
    <div className="relative w-fit">
      {/**
       * headlessui focuses first focusable element,
       * so using a button here with outline-none is our hack to control that
       */}
      <button
        type="button"
        onClick={() => closeModal()}
        className={`absolute right-0 top-0 cursor-pointer text-3xl 
          text-white outline-none sm:-right-1 sm:-top-8 md:top-0`}
      >
        <RxCross2 />
      </button>

      {/* content */}
      <div className="flex flex-col items-center justify-center sm:flex-row sm:items-start">
        {/* image */}
        <div
          className="relative mb-2 hidden duration-500 animate-in fade-in sm:block"
          key={displayedVariant.id}
        >
          <CardGalleryImage variant={displayedVariant} rotate={isSite} />
          {/* reverse flipper */}
          {hasReverse && <CardFlipper onFlip={toggleVariant} />}
        </div>

        {/* right section */}
        <div className="gallery-breakpoints sm:ml-6">
          <div className="hidden duration-500 animate-in fade-in sm:!block">
            <H3 className="!mb-2">{card.name}</H3>
            <div className="duration-500 animate-in fade-in">
              {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
              <P className="!mb-2 italic">{displayedVariant.vMeta!.typeText}</P>
              <P className="curiosa-quote !mb-2 opacity-80">
                {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
                {displayedVariant.setCard.meta!.rulesText}
              </P>
              <P className="opacity-80">{displayedVariant.artist.name}</P>
            </div>
          </div>

          {/* sm screen */}
          <H3 className="text-center sm:hidden">{card.name}</H3>
          <div
            className={
              "relative mb-8 flex flex-row justify-center duration-500 animate-in fade-in sm:mb-0 sm:hidden"
            }
            key={displayedVariant.id}
          >
            <CardGalleryImage variant={displayedVariant} rotate={isSite} />
            {/* reverse flipper */}
            {hasReverse && <CardFlipper onFlip={toggleVariant} />}
          </div>
          <Line className="mt-4 hidden !bg-line-color sm:block" />

          {/* deck quantities */}
          <div className="mb-2 mt-4 flex">
            {/* avatars cant go in decklist */}
            {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
            {card.guardian!.category !== CardCategoryEnum.Avatar && (
              <div className="mr-2">
                <P className="!mb-2 text-left">Decklist</P>
                <DeckQuantitySelector
                  quantity={decklistQuantity}
                  onChange={setDecklistQuantity}
                  doDebounce={false}
                  error={decklistQuantityMissingError}
                />
              </div>
            )}
            <div className="mr-2">
              <P className="!mb-2 text-left">Sideboard</P>
              <DeckQuantitySelector
                quantity={sideboardQuantity}
                onChange={setSideboardQuantity}
                doDebounce={false}
                error={sideboardQuantityMissingError}
              />
            </div>
            <div>
              <P className="!mb-2 text-left">Maybeboard</P>
              <DeckQuantitySelector
                quantity={maybeboardQuantity}
                onChange={setMaybeboardQuantity}
                doDebounce={false}
                error={maybeboardQuantityMissingError}
              />
            </div>
          </div>

          {/* switcher */}
          {card.variants.length > 1 ? (
            <VariantSwitcher
              variant={variant}
              variants={card.variants}
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              onChange={(v) => setVariant(card.variants.find((i) => i.id === v)!)}
            />
          ) : (
            <VariantLabel variant={displayedVariant} />
          )}

          {/* collection stuff */}
          <CollectionTrackingMessage
            itemsInCollection={itemsInCollection}
            itemsInDeckAndSideboard={decklistQuantity}
            className="pt-1"
          />

          <Line className="mb-2 mt-4 !bg-line-color" />
          {/* checkboxes */}
          {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
          {card.guardian!.category === CardCategoryEnum.Avatar && (
            <div
              className="items-top flex space-x-2"
              onClick={() => {
                if (isMyAvatar && avatar.variantId === variant.id) {
                  // if trying to unset with no replacement
                  toast.error("Can't set to blank. Please select a new avatar instead.");
                } else {
                  setIsMyAvatar(!isMyAvatar);
                }
              }}
            >
              <Checkbox id="is-avatar" checked={isMyAvatar} label="Avatar" />
            </div>
          )}

          <div
            className="items-top flex space-x-2 pt-2"
            onClick={() => {
              if (isFeature && deck.feature.id === variant.id) {
                // if trying to unset with no replacement
                toast.error("Can't set to blank. Please select a new feature instead.");
              } else {
                setIsFeature(!isFeature);
              }
            }}
          >
            <Checkbox id="is-feature" checked={isFeature} label="Feature image" />
          </div>

          {/* faqs */}
          {!!faqs?.length && (
            <div className="duration-500 animate-in fade-in">
              <Line className="hidden !bg-line-color sm:block" />
              <div className="mb-4 max-h-[160px] overflow-y-scroll gradient-mask-b-90 md:max-h-[200px] lg:max-h-[300px] xl:max-h-[320px]">
                <P className="!mb-2 text-left font-bold">
                  {faqs.length}
                  <span>&nbsp;FAQ</span>
                  {faqs.length > 1 && <span>s</span>}
                </P>
                <FAQList variant="sm" faqs={faqs} className="opacity-80" />
              </div>
            </div>
          )}

          {/* error */}
          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <Line className="my-4 !bg-line-color" />
      <div className="flex flex-row-reverse justify-between">
        <div className="flex flex-row-reverse gap-2">
          <Button onClick={onSave} className="min-w-[5rem]" disabled={isPending}>
            {isPending ? <>Saving...</> : <>Save</>}
          </Button>

          <Button onClick={closeModal} variant="outline">
            Cancel
          </Button>
        </div>
        <InternalLink
          href={cardPath(card.slug)}
          onClick={() => closeModal()}
          className="hover:text-primary cursor-pointer transition-colors"
        >
          Go to card
        </InternalLink>
      </div>
    </div>
  );
};
